import ReactDOM from "react-dom/client";
import App from "./App.tsx";
import {
  API_TAG,
  CLICK_ACTION,
  TRACK_EVENT,
  trackEvent,
} from "./utils/analytics";
import { initDeviceInfo } from "./utils/deviceInfo";
import { externalFeatures } from "./constant.ts";
import { useChatStore } from "./stores/chatStore";
import { envManager } from "./config/env";
import "./global.css";
import { getExternalEntryPath } from "./utils/getEntryPath.ts";

// 启用 MSW Mock 服务
async function enableMocking() {
  // 只有在明确启用 MSW 时才启动
  const env = import.meta.env || {};
  const { VITE_ENABLE_MSW } = env;
  if (VITE_ENABLE_MSW === "true") {
    const { worker } = await import("./mocks/browser");
    await worker.start({
      onUnhandledRequest: "bypass",
    });
    console.log("🚀 MSW Mock Service Worker started");
  }
}

// 全局变量存储应用实例
let appInstance: ReactDOM.Root | null = null;
let isMounted = false;

// 手动初始化应用
export async function initCasa({
  containerId,
  features,
  currentMarket,
}: {
  containerId?: string;
  currentMarket?: string;
  features?: {
    login: () => void;
    addToCart: ({
      product,
      variant,
      quantity,
    }: {
      product: any;
      variant: any;
      quantity: number;
    }) => Promise<any>;
    trackEvent: (
      eventName: string,
      eventParams: { [key: string]: any },
    ) => Promise<any>;
  };
} = {}) {
  if (isMounted) {
    console.warn("Casa is already mounted");
    return;
  }

  try {
    // 设置市场信息到环境管理器
    if (currentMarket) {
      envManager.setCurrentMarket(currentMarket);
      console.log(`Casa initialized with market: ${currentMarket}`);
    } else {
      const url = new URL(window.location.href);
      const market = url.pathname.split("/")[1];
      if (market) {
        envManager.setCurrentMarket(market);
        console.log(`Casa initialized with market: ${market}`);
      }
    }

    // 初始化 Chat Store Market
    useChatStore.getState().initMarket();

    // 启用 Mock 数据
    await enableMocking();

    // 初始化设备信息
    await initDeviceInfo();

    // 设置额外功能
    if (features) {
      externalFeatures.login = features.login;
      externalFeatures.addToCart = features.addToCart;
      externalFeatures.trackEvent = features.trackEvent;
    }

    // 获取或创建容器元素
    const containerIdToUse = containerId || "__pas__chat__";
    const containerClassName = "__pas__chat__container__";
    let root = document.getElementById(containerIdToUse);
    if (!root) {
      root = document.createElement("div");
      root.id = containerIdToUse;
      root.className = containerClassName;
      document.body.appendChild(root);
    }

    // 创建并渲染应用
    appInstance = ReactDOM.createRoot(root);
    appInstance.render(<App />);

    isMounted = true;
    console.log("Casa is mounted");
  } catch (error) {
    console.error("Casa mounted error:", error);
  }
}

// 销毁应用
export function destroyCasa() {
  if (appInstance) {
    appInstance.unmount();
    appInstance = null;
    isMounted = false;
    console.log("Casa destroyed");
  }
}

// 检查是否已初始化
export function isCasaMounted() {
  return isMounted;
}

// 实现一个 event bus，挂载到 __CASA_INSTANCE__ 上
class EventBus {
  private listeners: { [key: string]: Function[] } = {};

  on(event: string, callback: Function) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  off(event: string, callback?: Function) {
    if (!this.listeners[event]) return;

    if (!callback) {
      delete this.listeners[event];
      return;
    }

    this.listeners[event] = this.listeners[event].filter(
      (cb) => cb !== callback,
    );
  }

  emit(event: string, ...args: any[]) {
    if (!this.listeners[event]) return;
    this.listeners[event].forEach((callback) => callback(...args));
  }

  once(event: string, callback: Function) {
    const onceCallback = (...args: any[]) => {
      callback(...args);
      this.off(event, onceCallback);
    };
    this.on(event, onceCallback);
  }
}

class CasaInstance {
  private eventBus = new EventBus();
  on(event: string, callback: Function) {
    this.eventBus.on(event, callback);
  }
  off(event: string, callback?: Function) {
    this.eventBus.off(event, callback);
  }
  emit(event: string, ...args: any[]) {
    this.eventBus.emit(event, ...args);
  }
  once(event: string, callback: Function) {
    this.eventBus.once(event, callback);
  }
  openChat() {
    if (!isMounted) {
      console.warn("Casa is not mounted yet");
      return;
    }
    const { openChat, setEntryPath, setCurrentView } = useChatStore.getState();
    setEntryPath(getExternalEntryPath());
    setCurrentView("chatbox");
    openChat();
    trackEvent({
      event: TRACK_EVENT,
      category: "global",
      action: CLICK_ACTION,
      tag: API_TAG,
      tag_value: "chat_now",
    });
  }
}

// 将初始化函数暴露到全局对象
if (typeof window !== "undefined") {
  console.log("Casa loaded");
  window.__CASA_INSTANCE__ = new CasaInstance();
  window.initCasa = initCasa;
  window.destroyCasa = destroyCasa;
  window.isCasaMounted = isCasaMounted;
}

if (import.meta.env.DEV) {
  setTimeout(() => {
    if (window.initCasa) {
      window.initCasa({});
    }
  }, 1000);
}
